import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { priorityDownload } from 'reactApp/appHelpers/featuresHelpers/features/priorityDownload';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openBuyDisableAds, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { PriorityDownloadAnalytics } from 'reactApp/modules/priorityDownload/priorityDownload.analytics';
import { checkItemsPriority, getPriorityItemDwh } from 'reactApp/modules/priorityDownload/priorityDownload.helpers';
import { DownloadPriorityActions } from 'reactApp/modules/priorityDownload/priorityDownload.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { closePriorityDownloadModal, openPriorityDownloadModal } from 'reactApp/ui/PriorityDownloadModal/PriorityDownloadModal.helpers';
import { channel } from 'redux-saga';
import { cancel, delay, race, take, takeEvery } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

function* handleCheckDownloadPriority(action: ReturnType<typeof DownloadPriorityActions.checkDownloadPriority>) {
    const { continueDownload, item } = action.payload;

    const isMobile = EnvironmentSelectors.isMobile();
    const isPaidUser = yield* select(UserSelectors.isPaidUser);

    /**
     * Не задерживаем, если:
     * - мы в таче,
     * - платник, корп или биз
     * - вне экспа,
     *
     * А так же если скачиваемое не подходит под критерии:
     * - папка
     * - пачка файлов (>2)
     * - большой файл (больше 1 ГБ)
     **/
    if (isMobile || isPaidUser || !priorityDownload.enabled || !checkItemsPriority(item)) {
        continueDownload();
        yield cancel();
    }

    const dwhData = yield* getPriorityItemDwh(item);
    const paymentChannel = channel();

    const showCheckout = (product: Product) =>
        store.dispatch(
            openTariffBuy({
                productId: product.id,
                paySource: PriorityDownloadAnalytics.paySource,
                isMobile,
                onSuccess() {
                    emitAnalyticEvent(AnalyticEventNames.TARIFF_BUY, { paySource: PriorityDownloadAnalytics.paySource, product });
                    closePopupHelper(popupNames.BUY_DISABLE_ADS);
                    paymentChannel.put(true);
                },
            })
        );

    const showTariffsModal = () => {
        emitAnalyticEvent(AnalyticEventNames.PRIORITY_DOWNLOAD_CLICK);
        PriorityDownloadAnalytics.sendAnalytic({ action: 'click', dwhData });
        store.dispatch(
            openBuyDisableAds({
                title: priorityDownload.buyTitle,
                tariffsNumber: priorityDownload.tariffsNumber,
                activeTab: priorityDownload.activeTab,
                isFrame: false,
                onBuyClick: showCheckout,
            })
        );
    };

    yield openPriorityDownloadModal({
        onShow: () => {
            emitAnalyticEvent(AnalyticEventNames.PRIORITY_DOWNLOAD_SHOW);
            PriorityDownloadAnalytics.sendAnalytic({ action: 'show', dwhData });
        },
        onBuy: showTariffsModal,
        onFinishStep: closePriorityDownloadModal,
    });

    // ждем или покупки или завершения таймера
    yield race({
        posts: take(paymentChannel),
        timeout: delay(priorityDownload.delaySec * 1000),
    });

    // может быть рассинхрон, когда открыт фрейм окна оплаты, поэтому закрываем, если окно все еще открыто
    yield closePriorityDownloadModal();

    continueDownload();
    yield cancel();
}

export function* watchPriorityDownload() {
    yield takeEvery(DownloadPriorityActions.checkDownloadPriority.toString(), handleCheckDownloadPriority);
}
